import { Component } from '@theme/component';

/**
 * @typedef {object} ComparatorRefs
 * @property {HTMLDetailsElement[]} [selects] - Custom dropdowns, one per slot
 * @property {HTMLElement[]} [selectLabels] - Trigger labels showing the slot's product title
 * @property {HTMLElement[]} [columns] - Product header cards (1 per product, only N active)
 * @property {HTMLElement} [stickySentinel] - Anchor for the sticky `data-stuck` flip
 * @property {HTMLElement} [stickyWrap] - The sticky wrapper that gains `data-stuck`
 * @property {HTMLElement} [liveRegion] - aria-live polite announcer for slot swaps
 */

/**
 * Side-by-side product comparator with swappable columns.
 *
 * State model:
 *  - `#slots[i]` = the product id displayed in slot i (i in [0, columnsDesktop))
 *  - DOM truth = `[data-active]` + `style="--slot: i"` on each column / cell
 *  - All N product columns and N cells per row are SSR'd; we only flip flags
 *
 * Picking a product already shown in another slot performs a swap, not an
 * insert: `[A, B, C]` + click slot 1 → C  ⇒  `[A, C, B]`.
 *
 * Keyboard nav inside the open `role="listbox"`: ArrowUp/Down move focus
 * across options, Home/End jump to first/last, Escape closes.
 *
 * @extends {Component<ComparatorRefs>}
 */
class AxProductComparator extends Component {
  /** @type {string[]} */
  #slots = [];

  /** @type {number} */
  #columnsDesktop = 3;

  /** @type {number} */
  #columnsMobile = 2;

  /** @type {IntersectionObserver | null} */
  #stickyObserver = null;

  /** @type {AbortController | null} */
  #accordionSyncController = null;

  /** @type {boolean} - Garde anti-récursion pour la sync des accordions */
  #syncingAccordions = false;

  /** @type {ResizeObserver | null} */
  #rowSyncObserver = null;

  /** @type {number | null} */
  #rowSyncRafId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#hydrateState();
    this.#applyMobileVisibility();
    this.#initStickyObserver();
    this.#initAccordionSync();
    this.#initRowHeightSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;
    this.#accordionSyncController?.abort();
    this.#accordionSyncController = null;
    this.#rowSyncObserver?.disconnect();
    this.#rowSyncObserver = null;
    if (this.#rowSyncRafId != null) cancelAnimationFrame(this.#rowSyncRafId);
  }

  /**
   * Sync les `<details>` accordions entre colonnes : un clic sur une summary
   * ouvre/ferme la même catégorie (via `data-category-id`) dans toutes les
   * autres colonnes — DANS LA MÊME FRAME que le toggle natif, pour que les
   * animations soient parfaitement synchronisées.
   *
   * Pour atteindre la synchro :
   *  1. On intercepte `click` en `capture` (avant le toggle natif du browser).
   *  2. On `preventDefault()` pour bloquer le toggle automatique du browser.
   *  3. On set `peer.open = newState` sur les 3 details simultanément.
   *  4. Tous les transitions CSS démarrent dans la même frame de paint.
   *
   * Compatible clavier : Enter/Space sur `<summary>` déclenchent aussi un
   * `click` → même code path.
   */
  #initAccordionSync() {
    this.#accordionSyncController = new AbortController();
    const { signal } = this.#accordionSyncController;
    this.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        // Trouve la summary cliquée
        const summary = target.closest('summary');
        if (!(summary instanceof HTMLElement)) return;
        const details = summary.parentElement;
        if (!(details instanceof HTMLDetailsElement)) return;
        const categoryId = details.dataset.categoryId;
        if (!categoryId) return;
        // Empêche le toggle natif — on va le faire nous-même sur les 3 details
        // dans la même frame, pour synchroniser les animations.
        event.preventDefault();
        const newOpen = !details.open;
        for (const peer of this.querySelectorAll(
          `details[data-category-id="${categoryId}"]`
        )) {
          if (!(peer instanceof HTMLDetailsElement)) continue;
          if (peer.open !== newOpen) peer.open = newOpen;
        }
        // Après l'ouverture/fermeture, on re-mesure les rows à la frame
        // suivante (transition `block-size: 0 → auto` a démarré ; le layout
        // final est calculable au layout flush via getBoundingClientRect()).
        this.#scheduleRowHeightSync();
      },
      { capture: true, signal }
    );
  }

  /**
   * Aligne les hauteurs des feature-items entre colonnes row-par-row.
   *
   * Le problème : chaque colonne (= produit) a son propre `<ul>` de features
   * dans son accordion. Si la feature N dans colonne A wrappe sur 3 lignes
   * (texte long) et que dans colonne B la même feature N est juste "—"
   * (produit ne l'a pas), les 2 rows n'ont pas la même hauteur → décalage
   * visuel des lignes entre colonnes.
   *
   * Solution : on mesure le max de hauteur naturelle pour chaque index de
   * row à travers les colonnes actives, puis on set `min-height` sur tous
   * les items de cet index. CSS-only ne marche pas car les `<ul>` sont
   * dans des sous-arbres distincts (1 par accordion × colonne) → pas de
   * grid commun pour aligner les rows.
   *
   * Déclenché :
   *  - après ouverture/fermeture d'un accordion (mesure quand visible)
   *  - après un swap de produit (heights changent avec le nouveau produit)
   *  - sur resize (recalcul si les colonnes changent de largeur)
   */
  #initRowHeightSync() {
    // ResizeObserver sur le custom element pour resync au resize
    this.#rowSyncObserver = new ResizeObserver(() => {
      this.#scheduleRowHeightSync();
    });
    this.#rowSyncObserver.observe(this);
    // Sync initial pour les accordions déjà ouverts (is_expanded_default)
    this.#scheduleRowHeightSync();
    // Resync après chargement des webfonts (le nb de lignes des titres change)
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#scheduleRowHeightSync();
    });
  }

  #scheduleRowHeightSync() {
    if (this.#rowSyncRafId != null) cancelAnimationFrame(this.#rowSyncRafId);
    this.#rowSyncRafId = requestAnimationFrame(() => {
      this.#rowSyncRafId = null;
      this.#syncColumnTitleHeights();
      this.#syncRowHeights();
    });
  }

  /**
   * Égalise la hauteur des titres entre colonnes visibles : un titre plus long
   * (multi-lignes) décalerait sinon tout ce qui suit dans sa colonne.
   * `offsetParent !== null` exclut les colonnes inactives / masquées mobile.
   */
  #syncColumnTitleHeights() {
    /** @type {HTMLElement[]} */
    const titles = [];
    for (const col of this.querySelectorAll('.ax-product-comparator__column[data-active]')) {
      if (!(col instanceof HTMLElement) || col.offsetParent === null) continue;
      const title = col.querySelector('.ax-product-comparator__column-title');
      if (title instanceof HTMLElement) titles.push(title);
    }

    // Reset min-height pour mesurer la hauteur naturelle (respecte le 2lh CSS)
    for (const title of titles) title.style.minHeight = '';
    if (titles.length < 2) return;

    let max = 0;
    for (const title of titles) {
      max = Math.max(max, title.getBoundingClientRect().height);
    }
    if (max <= 0) return;

    for (const title of titles) title.style.minHeight = `${max}px`;
  }

  #syncRowHeights() {
    /** @type {Set<string>} */
    const openCategoryIds = new Set();
    for (const d of this.querySelectorAll(
      '.ax-product-comparator__column[data-active]:not([data-mobile-hidden]) details[data-category-id][open]'
    )) {
      if (d instanceof HTMLElement && d.dataset.categoryId) {
        openCategoryIds.add(d.dataset.categoryId);
      }
    }

    for (const catId of openCategoryIds) {
      const lists = this.querySelectorAll(
        `.ax-product-comparator__column[data-active]:not([data-mobile-hidden]) details[data-category-id="${catId}"] .ax-product-comparator__feature-list`
      );
      if (lists.length < 2) continue;

      // Reset min-height pour mesurer la hauteur naturelle
      /** @type {HTMLElement[][]} */
      const allItems = [];
      for (const list of lists) {
        /** @type {HTMLElement[]} */
        const items = [];
        for (const el of list.querySelectorAll('.ax-product-comparator__feature-item')) {
          if (el instanceof HTMLElement) {
            el.style.minHeight = '';
            items.push(el);
          }
        }
        allItems.push(items);
      }

      // Mesure max height par index de row
      /** @type {number[]} */
      const rowMax = [];
      for (const items of allItems) {
        for (let i = 0; i < items.length; i++) {
          const h = items[i].getBoundingClientRect().height;
          rowMax[i] = Math.max(rowMax[i] ?? 0, h);
        }
      }

      // Applique min-height aligné sur tous les items de cet index
      for (const items of allItems) {
        for (let i = 0; i < items.length; i++) {
          const max = rowMax[i];
          if (max != null && max > 0) {
            items[i].style.minHeight = `${max}px`;
          }
        }
      }
    }
  }

  /**
   * Read the initial slot mapping from the data attribute and cache the
   * column counts. The values come from Liquid so we trust them as strings;
   * mobile is always a subset of desktop (mobile <= desktop is enforced by
   * the schema).
   */
  #hydrateState() {
    const raw = this.dataset.initialSlots ?? '';
    this.#slots = raw.split(',').map((v) => v.trim()).filter(Boolean);
    this.#columnsDesktop = Number(this.dataset.columnsDesktop) || 3;
    this.#columnsMobile = Number(this.dataset.columnsMobile) || 2;
  }

  /**
   * Mark active columns/cells beyond `columnsMobile - 1` with
   * `data-mobile-hidden="true"` so the CSS `@media (max-width: 749px)` rule
   * collapses them. We do this from JS rather than baking it into Liquid
   * because the slot mapping changes after user interaction.
   */
  #applyMobileVisibility() {
    const mobileMax = this.#columnsMobile;
    const activeProductIds = new Set(this.#slots.slice(0, this.#columnsDesktop));

    const apply = (/** @type {HTMLElement} */ el) => {
      const productId = el.dataset.productId;
      if (!productId || !activeProductIds.has(productId)) return;
      const slot = this.#slots.indexOf(productId);
      if (slot < 0) return;
      if (slot >= mobileMax) {
        el.setAttribute('data-mobile-hidden', 'true');
      } else {
        el.removeAttribute('data-mobile-hidden');
      }
    };

    // Product columns (each contains image+info+ctas + its own accordions)
    for (const col of this.querySelectorAll('.ax-product-comparator__column[data-active]')) {
      if (col instanceof HTMLElement) apply(col);
    }
  }

  /**
   * Watch a 1px sentinel placed just above the sticky wrap. When it leaves
   * the viewport the wrap has stuck — we flip `data-stuck` to drive the
   * shadow / padding animation. Cheaper and smoother than a scroll listener.
   */
  #initStickyObserver() {
    const sentinel = this.refs.stickySentinel;
    const wrap = this.refs.stickyWrap;
    if (!(sentinel instanceof HTMLElement) || !(wrap instanceof HTMLElement)) return;

    this.#stickyObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          wrap.setAttribute('data-stuck', entry.isIntersecting ? 'false' : 'true');
        }
      },
      { threshold: [0] }
    );
    this.#stickyObserver.observe(sentinel);
  }

  /**
   * Public event handler — fires when the user picks a product from a slot's
   * dropdown. Wired via `on:click="/handleSelect/{productId}"` (un seul path
   * arg supporté par le Component framework, voir `assets/component.js:248`).
   *
   * L'index du slot est récupéré depuis `data-slot-index` sur le parent
   * `.custom-select` plutôt que dans le path.
   *
   * Swap semantics :
   *  - Si le produit choisi est déjà dans un autre slot j ≠ i → swap i ↔ j
   *  - Sinon                                                  → slots[i] = X
   *  - Si même slot (re-click) → on ferme juste le dropdown
   *
   * @param {string|number} productIdData - id de l'option cliquée (peut être
   *   parsé en number par le framework si l'id est purement numérique)
   * @param {Event} event - événement click (utilisé pour retrouver le slot)
   */
  handleSelect(productIdData, event) {
    const productId = String(productIdData);
    if (!productId) return;

    const triggerEl = event?.target instanceof Element ? event.target : null;
    const detailsEl = triggerEl?.closest('.custom-select');
    if (!(detailsEl instanceof HTMLDetailsElement)) return;

    const i = Number(detailsEl.dataset.slotIndex);
    if (!Number.isFinite(i) || i < 0 || i >= this.#columnsDesktop) return;

    const j = this.#slots.indexOf(productId);
    if (j === i) {
      this.#closeDropdown(i);
      return;
    }

    if (j !== -1) {
      [this.#slots[i], this.#slots[j]] = [this.#slots[j], this.#slots[i]];
    } else {
      this.#slots[i] = productId;
    }

    // Désactive les transitions sur les accordions pendant le swap.
    // Sans ça : quand la colonne qui apparaît passe de `display: none` à
    // `display: flex`, le browser ré-applique `@starting-style` sur les
    // `<details open>` → animation depuis `block-size: 0` même si la
    // catégorie était déjà ouverte avant. Effet visuel "fermé → ouvert"
    // alors que l'état logique n'a pas changé.
    this.setAttribute('data-swapping', '');
    this.#applySlotsToDom();
    this.#applyMobileVisibility();
    this.#syncDropdownTriggers();
    this.#closeDropdown(i);
    this.#announceSwap();
    // 2× rAF garantit que le layout + paint suivants se sont terminés
    // sans transition, puis on réactive pour les toggles utilisateur.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.removeAttribute('data-swapping');
        // Le nouveau produit peut avoir des features différentes (textes
        // plus ou moins longs) → on resync les hauteurs de rows.
        this.#scheduleRowHeightSync();
      });
    });
  }

  /**
   * Public event handler — fires on `keydown` over a focused option button
   * inside an open dropdown. Wired via `on:keydown="/handleOptionKeydown"`.
   *
   * Implements the WAI-ARIA listbox keyboard pattern:
   *  - ArrowUp / ArrowDown : move focus to previous / next option
   *  - Home / End          : focus first / last option
   *  - Escape              : close dropdown, return focus to trigger
   *
   * Tab is left to the browser (cycles through option buttons).
   *
   * @param {KeyboardEvent} event
   */
  handleOptionKeydown(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const detailsEl = target.closest('.custom-select');
    if (!(detailsEl instanceof HTMLDetailsElement)) return;

    const options = Array.from(
      detailsEl.querySelectorAll('.custom-select__option')
    ).filter((el) => el instanceof HTMLElement);

    const currentIndex = options.indexOf(target);
    if (currentIndex < 0) return;

    let nextIndex = -1;
    switch (event.key) {
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % options.length;
        break;
      case 'ArrowUp':
        nextIndex = currentIndex === 0 ? options.length - 1 : currentIndex - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = options.length - 1;
        break;
      case 'Escape': {
        event.preventDefault();
        detailsEl.removeAttribute('open');
        const trigger = detailsEl.querySelector('summary');
        if (trigger instanceof HTMLElement) trigger.focus();
        return;
      }
      default:
        return;
    }

    event.preventDefault();
    const next = options[nextIndex];
    if (next instanceof HTMLElement) next.focus();
  }

  /**
   * Reapply `data-active` + `--slot` to columns and cells so the CSS grid
   * shows the right products in the right order. Cells that were active
   * before but whose product is no longer in the slots get cleared first.
   */
  #applySlotsToDom() {
    /** @type {Map<string, number>} */
    const activeIds = new Map();
    for (let i = 0; i < this.#slots.length && i < this.#columnsDesktop; i += 1) {
      const id = this.#slots[i];
      if (id) activeIds.set(id, i);
    }

    const refresh = (/** @type {NodeListOf<Element>} */ nodes) => {
      for (const node of nodes) {
        if (!(node instanceof HTMLElement)) continue;
        const id = node.dataset.productId;
        if (!id) continue;

        if (activeIds.has(id)) {
          node.setAttribute('data-active', '');
          node.style.setProperty('--slot', String(activeIds.get(id)));
        } else {
          node.removeAttribute('data-active');
          node.style.removeProperty('--slot');
          node.removeAttribute('data-mobile-hidden');
        }
      }
    };

    refresh(this.querySelectorAll('.ax-product-comparator__column'));
  }

  /**
   * Update each dropdown's visible label and per-option `aria-selected` so
   * AT clients see the current state and the closed trigger reads the
   * picked product without requiring a page repaint.
   */
  #syncDropdownTriggers() {
    const selects = this.querySelectorAll('.custom-select');
    for (const detailsEl of selects) {
      if (!(detailsEl instanceof HTMLDetailsElement)) continue;

      const slotIndex = Number(detailsEl.dataset.slotIndex);
      if (!Number.isFinite(slotIndex)) continue;

      const activeId = this.#slots[slotIndex];
      const label = detailsEl.querySelector('.custom-select__label');
      const options = detailsEl.querySelectorAll('.custom-select__option');

      let activeTitle = '';
      for (const opt of options) {
        if (!(opt instanceof HTMLElement)) continue;
        const isActive = opt.dataset.optionId === activeId;
        opt.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (isActive) activeTitle = opt.dataset.optionLabel ?? '';
      }
      if (label instanceof HTMLElement && activeTitle) {
        label.textContent = activeTitle;
      }
    }
  }

  /**
   * Close the dropdown of slot `i` after a successful pick. Native
   * `<details>` accumulates `open` from the click before our handler ran,
   * so we just remove the attribute here.
   * @param {number} i
   */
  #closeDropdown(i) {
    const select = this.querySelector(
      `.custom-select[data-slot-index="${i}"]`
    );
    if (select instanceof HTMLDetailsElement) select.removeAttribute('open');
  }

  /**
   * Tell screen readers what changed. We keep the message short (just the
   * new combo of titles) — the polite live region will queue without
   * interrupting the current reading.
   */
  #announceSwap() {
    const live = this.refs.liveRegion;
    if (!(live instanceof HTMLElement)) return;

    const titles = [];
    for (const id of this.#slots.slice(0, this.#columnsDesktop)) {
      const opt = this.querySelector(
        `.custom-select__option[data-option-id="${id}"]`
      );
      if (opt instanceof HTMLElement) {
        titles.push(opt.dataset.optionLabel ?? '');
      }
    }
    live.textContent = titles.filter(Boolean).join(' · ');
  }
}

customElements.define('ax-product-comparator', AxProductComparator);
